// ─────────────────────────────────────────────────────────────────────────────
// Amend a soft-locked invoice (open / partially_paid) without un-completing the
// booking. The staged edits are sent to the amend_booking_invoice RPC, which
// applies them to the booking tables, rebuilds the invoice via the existing
// sync machinery, and re-issues it under the same number — one transaction.
// Fully paid invoices are hard-locked and refused by the RPC.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from '../supabase';
import { getInvoiceForBooking, type BookingInvoice } from './getInvoiceForBooking';

export interface AmendableAddon {
  id: string;
  name: string;
  price: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
}

export interface AmendableService {
  id: string;
  name: string;
  description: string | null;
  price: number;
  quantity: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  addons: AmendableAddon[];
  vehicle_id: string | null;
  /** e.g. "2021 Toyota Tacoma" — empty when the booking has no vehicle */
  vehicle_label: string;
  vehicle_color: string | null;
}

export interface AmendableProduct {
  id: string;
  stock_item_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
}

export interface AmendableDiscount {
  type: 'percentage' | 'fixed';
  amount: number;
  title: string;
}

export interface AmendableBilling {
  invoice: BookingInvoice;
  services: AmendableService[];
  products: AmendableProduct[];
  discount: AmendableDiscount | null;
}

/** Current billing state of the booking (source of truth the amend edits). */
export async function fetchAmendableBilling(bookingId: string): Promise<AmendableBilling> {
  const invoice = await getInvoiceForBooking(bookingId);
  if (!invoice) throw new Error('No invoice found for this booking');

  const [servicesRes, productsRes, discountRes] = await Promise.all([
    supabase
      .from('booking_services')
      .select(
        'id, name, description, price, quantity, discount_type, discount_value, vehicle_id, ' +
        'vehicle:customer_vehicles(make, model, year, color), ' +
        'addons:booking_service_addons(id, price, discount_type, discount_value, addon:service_addons(name))',
      )
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: true }),
    supabase
      .from('product_booking_sales')
      .select('id, stock_item_id, product_name, quantity, unit_price')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: true }),
    supabase
      .from('booking_discounts')
      .select('title, type, amount')
      .eq('booking_id', bookingId)
      .maybeSingle(),
  ]);

  if (servicesRes.error) throw servicesRes.error;
  if (productsRes.error) throw productsRes.error;

  const services: AmendableService[] = (servicesRes.data || []).map((s: any) => ({
    id: s.id,
    name: s.name,
    description: s.description || null,
    price: Number(s.price) || 0,
    quantity: Number(s.quantity) || 1,
    discount_type: s.discount_type === 'percentage' ? 'percentage' : 'fixed',
    discount_value: Number(s.discount_value) || 0,
    addons: (s.addons || []).map((a: any) => ({
      id: a.id,
      name: a.addon?.name || 'Add-on',
      price: Number(a.price) || 0,
      discount_type: a.discount_type === 'percentage' ? 'percentage' : 'fixed',
      discount_value: Number(a.discount_value) || 0,
    })),
    vehicle_id: s.vehicle_id ?? null,
    vehicle_label: s.vehicle
      ? [s.vehicle.year, s.vehicle.make, s.vehicle.model].filter(Boolean).join(' ')
      : '',
    vehicle_color: s.vehicle?.color ?? null,
  }));

  const products: AmendableProduct[] = (productsRes.data || []).map((p: any) => ({
    id: p.id,
    stock_item_id: p.stock_item_id,
    product_name: p.product_name,
    quantity: Number(p.quantity) || 1,
    unit_price: Number(p.unit_price) || 0,
  }));

  const discount: AmendableDiscount | null = discountRes.data
    ? {
        type: discountRes.data.type as 'percentage' | 'fixed',
        amount: Number(discountRes.data.amount) || 0,
        title: discountRes.data.title || 'Discount',
      }
    : null;

  return { invoice, services, products, discount };
}

/** Staged change payload — mirrors the amend_booking_invoice RPC contract. */
export interface AmendChanges {
  service_updates?: {
    id: string;
    price?: number;
    quantity?: number;
    discount_type?: 'percentage' | 'fixed';
    discount_value?: number;
  }[];
  service_deletes?: string[];
  /** Shape mirrors the booking info modal's own booking_services insert. */
  service_adds?: {
    service_id: string;
    price: number;
    quantity?: number;
    name?: string;
    description?: string;
    vehicle_id?: string | null;
    tax_exempt?: boolean;
    tax_rate?: number;
    pricing_type?: string;
    unit_price?: number;
    duration_per_unit?: number;
    duration_minutes?: number;
  }[];
  addon_updates?: {
    id: string;
    price?: number;
    discount_type?: 'percentage' | 'fixed';
    discount_value?: number;
  }[];
  addon_deletes?: string[];
  product_updates?: { id: string; quantity?: number; unit_price?: number }[];
  product_deletes?: string[];
  product_adds?: { stock_item_id: string; quantity?: number; unit_price?: number }[];
  discount?:
    | { action: 'set'; type: 'percentage' | 'fixed'; amount: number; title?: string }
    | { action: 'remove' };
}

export interface AmendResult {
  invoice_id: string;
  status: string;
  total_before_cents: number;
  total_after_cents: number;
  amount_paid_cents: number;
}

export async function amendBookingInvoice(
  bookingId: string,
  changes: AmendChanges,
  reason?: string,
): Promise<AmendResult> {
  const { data, error } = await supabase.rpc('amend_booking_invoice', {
    p_booking_id: bookingId,
    p_changes: changes,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message || 'Failed to amend invoice');
  return data as AmendResult;
}

/** Catalog options for the "add" pickers in the amend editor. */
export async function fetchAmendCatalogs(businessId: string): Promise<{
  services: { id: string; name: string }[];
  products: { id: string; name: string; price: number }[];
}> {
  const [servicesRes, productsRes] = await Promise.all([
    supabase.from('services').select('id, name').eq('business_id', businessId).order('name'),
    supabase.from('stock_items').select('id, name, price').eq('business_id', businessId).order('name'),
  ]);
  return {
    services: (servicesRes.data || []) as { id: string; name: string }[],
    products: ((productsRes.data || []) as any[]).map((p) => ({
      id: p.id,
      name: p.name,
      price: Number(p.price) || 0,
    })),
  };
}
